import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import { Fab, Tooltip, Zoom, useTheme } from '@mui/material';
import React, { useEffect, useCallback } from 'react';

import { useHelpDrawer } from '../../context/HelpDrawerContext';
import isAuthenticated from '../../utils/authUtils';
import { MOBILE_BOTTOM_NAV_HEIGHT } from '../layout/chromeSizes';

/**
 * GlobalHelpButton - Floating action button that toggles the Help Center drawer
 *
 * Features:
 * - Fixed position in bottom-right corner
 * - Only renders for authenticated users
 * - Keyboard shortcut support (F1 to toggle)
 * - Zoom animation when drawer state changes
 */
const GlobalHelpButton: React.FC = () => {
  const theme = useTheme();
  const { isOpen, toggle } = useHelpDrawer();

  // Handle keyboard shortcut (F1)
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'F1') {
        event.preventDefault();
        toggle();
      }
    },
    [toggle]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Only render for authenticated users
  if (!isAuthenticated()) {
    return null;
  }

  return (
    <Zoom in={!isOpen}>
      <Tooltip title="Help Center (F1)" placement="left">
        <Fab
          color="info"
          onClick={toggle}
          size="medium"
          aria-label="Open Help Center"
          sx={{
            position: 'fixed',
            bottom: theme.spacing(3),
            right: theme.spacing(3),
            zIndex: theme.zIndex.speedDial,
            // HA-481: the bottom nav sits above this in the stack, so it has to clear it.
            [theme.breakpoints.down('md')]: {
              bottom: `calc(${MOBILE_BOTTOM_NAV_HEIGHT}px + ${theme.spacing(2)} + env(safe-area-inset-bottom))`,
              right: theme.spacing(2),
            },
          }}
        >
          <HelpOutlineIcon />
        </Fab>
      </Tooltip>
    </Zoom>
  );
};

export default GlobalHelpButton;
